/**
 * components/client-detail/DossierPieceRow.tsx
 *
 * Individual sortable row in the "Dans le dossier" list.
 * Shows drag handle, piece number, editable label, up/down reorder buttons.
 * Always expands to show the list of included documents.
 * Each document has its own drag handle and up/down buttons for within-piece reordering,
 * plus Eye (preview) and X (remove) buttons.
 *
 * Updated Apr 2026 (per-doc model):
 * - Replaced onExcludePiece with onExcludeDoc + onPreviewDoc
 * - Always shows document list (no expand/collapse toggle)
 * - Removed piece-level X button
 * Updated: replaced hardcoded zIndex 10 with Z_INDEX.DRAG from shared constants.
 * Updated Apr 2026: added inner DndContext + SortableContext for per-file reordering.
 * Uses nested DndContext (isolated from the outer piece-level DndContext).
 * Updated Jun 2026: passes availableFields, onReclassify, onRename to DossierDocRow.
 */

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Z_INDEX } from '../../constants/zIndex';
import { GripVertical, ChevronUp, ChevronDown, Pencil } from 'lucide-react';
import type { DossierPiece, DocumentEntry } from '../../types/dossier';
import type { AvailableField } from './ClientDocumentsTab';
import DossierDocRow from './DossierDocRow';

interface DossierPieceRowProps {
  piece: DossierPiece;
  index: number;
  total: number;
  availableFields: AvailableField[];
  onLabelChange: (id: string, label: string) => void;
  onMove: (id: string, direction: 'up' | 'down') => void;
  onExcludeDoc: (docId: string) => void;
  onPreviewDoc: (doc: DocumentEntry) => void;
  onReorderDocs: (pieceId: string, orderedDocIds: string[]) => void;
  onMoveDoc: (pieceId: string, docId: string, direction: 'up' | 'down') => void;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onRename: (docId: string, newName: string) => Promise<boolean>;
}

export default function DossierPieceRow({
  piece,
  index,
  total,
  availableFields,
  onLabelChange,
  onMove,
  onExcludeDoc,
  onPreviewDoc,
  onReorderDocs,
  onMoveDoc,
  onReclassify,
  onRename,
}: DossierPieceRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: piece.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? Z_INDEX.DRAG : undefined,
    opacity: isDragging ? 0.8 : 1,
  };

  const docSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDocDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = piece.docs.findIndex((d) => d.id === active.id);
    const newIndex = piece.docs.findIndex((d) => d.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = [...piece.docs];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    onReorderDocs(piece.id, reordered.map((d) => d.id));
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border transition-all shadow-soft ${
        isDragging
          ? 'border-primary-300 shadow-soft-lg'
          : 'border-neutral-200 hover:border-neutral-300'
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-neutral-400 hover:text-neutral-600 transition-colors shrink-0 touch-none"
          {...attributes}
          {...listeners}
          aria-label="Réorganiser"
        >
          <GripVertical className="w-5 h-5" />
        </button>

        <span className="w-6 h-6 shrink-0 flex items-center justify-center rounded-full bg-primary-50 text-primary-700 text-xs font-bold">
          {index + 1}
        </span>

        <div className="flex-1 min-w-0">
          <div className="group relative flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 hover:border-primary-300 hover:bg-white focus-within:border-primary-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-100 transition-all">
            <input
              type="text"
              value={piece.label}
              onChange={(e) => onLabelChange(piece.id, e.target.value)}
              className="w-full text-sm font-medium text-neutral-900 bg-transparent border-none outline-none truncate"
              aria-label={`Label de la pièce ${index + 1}`}
            />
            <Pencil className="w-3 h-3 shrink-0 text-neutral-300 group-hover:text-primary-400 group-focus-within:text-primary-500 transition-colors" />
          </div>
        </div>

        <div className="flex flex-col gap-0.5 shrink-0">
          <button
            type="button"
            onClick={() => onMove(piece.id, 'up')}
            disabled={index === 0}
            className="p-1 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            aria-label="Monter"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(piece.id, 'down')}
            disabled={index === total - 1}
            className="p-1 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            aria-label="Descendre"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {piece.docs.length > 0 && (
        <div className="border-t border-neutral-100 pb-2">
          <DndContext
            sensors={docSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDocDragEnd}
          >
            <SortableContext
              items={piece.docs.map((d) => d.id)}
              strategy={verticalListSortingStrategy}
            >
              {piece.docs.map((doc, docIndex) => (
                <DossierDocRow
                  key={doc.id}
                  doc={doc}
                  docIndex={docIndex}
                  docsTotal={piece.docs.length}
                  availableFields={availableFields}
                  onPreviewDoc={onPreviewDoc}
                  onExcludeDoc={onExcludeDoc}
                  onMoveDoc={(docId, direction) => onMoveDoc(piece.id, docId, direction)}
                  onReclassify={onReclassify}
                  onRename={onRename}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}
